import { useMemo } from 'react';
import { MdOutlineClear, MdOutlineFilterAlt } from 'react-icons/md';
import { PiCalendarCheckBold, PiCalendarDotsBold } from 'react-icons/pi';
import { Button } from '@ds';
import { IconToggle, FilterDropdown } from '@ui';
import { HubsDataDictionary, WorkdaySyncData } from '@lp/features/Scheduler/types';
import { FilterOptionsDropdown, SchedulerResourcesFilter } from '@lp/features/Scheduler/components';
import { scheduleFiltersSelectors, useCSSchedulerFiltersStore } from '@lp/core/store/scheduler';
import { CSResourcesWithShifts } from '@/api/apis/resources/schedule';

type CSSchedulerFiltersSectionProps = {
  readonly?: boolean;
  shiftsData: CSResourcesWithShifts;
  hubsDictionary: HubsDataDictionary;
  workdayData?: WorkdaySyncData;
  isReservationsView: boolean;
  setIsReservationsView: (value: boolean) => void;
  showOrganFilters: boolean;
  setShowOrganFilters: (value: boolean) => void;
};

export const CSSchedulerFiltersSection = ({
  readonly,
  shiftsData,
  hubsDictionary,
  workdayData,
  isReservationsView,
  setIsReservationsView,
  showOrganFilters,
  setShowOrganFilters,
}: CSSchedulerFiltersSectionProps) => {
  const hasActiveFilters = useCSSchedulerFiltersStore(scheduleFiltersSelectors.hasActiveFilters);
  const filters = useCSSchedulerFiltersStore();
  const hubsFilterList = useMemo(() => {
    return Object.values(hubsDictionary).map((h) => ({ name: h.name, value: h.id }));
  }, [hubsDictionary]);

  const activeOrganFiltersCount = [
    filters.organs,
    filters.phoneSupportOrgans,
    filters.shadowOrgans,
    filters.proctorOrgans,
  ].filter((f) => f.length > 0).length;

  return (
    <div className="flex items-center gap-4">
      {hasActiveFilters && (
        <Button disableRipple size="sm" variant="light" endContent={<MdOutlineClear />} onClick={filters.clearFilters}>
          Clear all
        </Button>
      )}
      <SchedulerResourcesFilter
        shiftsData={shiftsData}
        selectedKeys={filters.resources}
        onSelectedKeysChange={(ids) => filters.setFilterByKey('resources', ids)}
      />
      <FilterDropdown
        title="Hub"
        mode="multiple"
        items={hubsFilterList}
        selectedKeys={filters.hubs}
        onSelectedKeysChange={(ids) => filters.setFilterByKey('hubs', ids)}
      />
      <Button
        disableRipple
        size="sm"
        variant="light"
        className="bg-base-55 ps-4 pe-2"
        endContent={<MdOutlineFilterAlt className="text-base-30" />}
        onPress={() => setShowOrganFilters(!showOrganFilters)}
      >
        <span className="text-m-m text-base-10">{showOrganFilters ? 'Hide Organ Filters' : 'Show Organ Filters'}</span>
        {activeOrganFiltersCount > 0 && (
          <span className="inline-flex items-center justify-center rounded-full bg-white text-base-10 text-sm w-6 h-[17px] py-0.5">
            {activeOrganFiltersCount}
          </span>
        )}
      </Button>
      <FilterOptionsDropdown
        onlyStatusFilter={readonly}
        withShiftTotals
        shiftTotalsEnabled={filters.shiftTotals}
        onShiftTotalsChange={(enable) => filters.setFilterByKey('shiftTotals', enable)}
        statusFilterEnabled={filters.statusFilters}
        onStatusFilterChange={(enable) => filters.setFilterByKey('statusFilters', enable)}
        workdayData={workdayData}
      />
      <IconToggle
        isSelected={isReservationsView}
        onValueChange={setIsReservationsView}
        enableIcon={<PiCalendarCheckBold />}
        disableIcon={<PiCalendarDotsBold />}
      />
    </div>
  );
};
